import { parseProfileShare } from "../domain/profileShare.js";
import { validShareToken } from "./shareToken.js";

const expirationTtl = 7 * 24 * 60 * 60;
const maximumPayloadLength = 1_000_000;

export async function loadSharedProfile(fetchRequest, token) {
  return requestSharedProfile(fetchRequest, "GET", token);
}

export async function saveSharedProfile(fetchRequest, token, share) {
  return requestSharedProfile(fetchRequest, "PUT", token, { share });
}

export async function handleSharedProfileRequest(request, kv) {
  if (!kv) return jsonResponse({ error: "SHARED_PROFILES is not configured." }, 503);
  const token = bearerToken(request.headers.get("Authorization"));
  if (!await validShareToken(token)) return jsonResponse({ error: "Invalid sharing token." }, 403);

  if (request.method === "GET") {
    const stored = await kv.get(token, { type: "json" });
    if (!stored) return jsonResponse({ error: "This shared profile was not found or has expired." }, 404);
    try {
      return jsonResponse({ ...stored, share: parseProfileShare(stored.share) });
    } catch {
      return jsonResponse({ error: "The stored profile is invalid." }, 500);
    }
  }

  if (request.method === "PUT") {
    let payload;
    try { payload = await request.json(); } catch { return jsonResponse({ error: "Send a valid JSON request." }, 400); }
    if (JSON.stringify(payload).length > maximumPayloadLength) return jsonResponse({ error: "The profile is too large to share." }, 413);
    let share;
    try { share = parseProfileShare(payload?.share); } catch (error) { return jsonResponse({ error: error.message }, 400); }
    const stored = { updatedAt: new Date().toISOString(), share };
    await kv.put(token, JSON.stringify(stored), { expirationTtl });
    return jsonResponse(stored);
  }

  return jsonResponse({ error: "Method not allowed." }, 405, { Allow: "GET, PUT" });
}

async function requestSharedProfile(fetchRequest, method, token, body) {
  if (!await validShareToken(token)) throw new Error("This sharing link is invalid.");
  const response = await fetchRequest("/api/shared-profile", {
    method,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    body: body ? JSON.stringify(body) : undefined
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error ?? `Request failed (${response.status}).`);
  return { ...result, share: parseProfileShare(result.share) };
}

function bearerToken(value) { return value?.startsWith("Bearer ") ? value.slice("Bearer ".length) : ""; }
function jsonResponse(value, status = 200, headers = {}) { return new Response(JSON.stringify(value), { status, headers: { "Cache-Control": "no-store", "Content-Type": "application/json", ...headers } }); }
